import { useState, useCallback } from "react";

export interface ModalState {
  addCategory: boolean;
  addSubcategory: boolean;
  addTertiary: boolean;
  additionalFields: boolean;
  editCategory: boolean;
  editSubcategory: boolean;
  editTertiary: boolean;
  deleteConfirmation: boolean;
}

export interface SelectedContext {
  category: string;
  subcategory: string;
  tertiary?: string;
  modalType?: "category" | "subcategory" | "tertiary";
  editingItem?: any;
  deleteItemType?: "category" | "subcategory" | "tertiary";
}

export const useTicketingConfigModals = () => {
  const [modals, setModals] = useState<ModalState>({
    addCategory: false,
    addSubcategory: false,
    addTertiary: false,
    additionalFields: false,
    editCategory: false,
    editSubcategory: false,
    editTertiary: false,
    deleteConfirmation: false,
  });

  const [selectedContext, setSelectedContext] = useState<SelectedContext>({
    category: "",
    subcategory: "",
  });

  const openAddCategoryModal = useCallback(() => {
    setModals((prev) => ({ ...prev, addCategory: true }));
  }, []);

  const closeAddCategoryModal = useCallback(() => {
    setModals((prev) => ({ ...prev, addCategory: false }));
  }, []);

  const openAddSubcategoryModal = useCallback((category: string) => {
    setSelectedContext({
      category,
      subcategory: "",
      modalType: "subcategory",
    });
    setModals((prev) => ({ ...prev, addSubcategory: true }));
  }, []);

  const closeAddSubcategoryModal = useCallback(() => {
    setModals((prev) => ({ ...prev, addSubcategory: false }));
  }, []);

  const openAddTertiaryModal = useCallback(
    (category: string, subcategory: string) => {
      setSelectedContext({
        category,
        subcategory,
        modalType: "tertiary",
      });
      setModals((prev) => ({ ...prev, addTertiary: true }));
    },
    []
  );

  const closeAddTertiaryModal = useCallback(() => {
    setModals((prev) => ({ ...prev, addTertiary: false }));
  }, []);

  const openAdditionalFieldsModal = useCallback(() => {
    setModals((prev) => ({ ...prev, additionalFields: true }));
  }, []);

  const openCategoryFieldsModal = useCallback((category: string) => {
    setSelectedContext({ category, subcategory: "", modalType: "category" });
    setModals((prev) => ({ ...prev, additionalFields: true }));
  }, []);

  const openSubcategoryFieldsModal = useCallback((category: string, subcategory: string) => {
    setSelectedContext({ category, subcategory, modalType: "subcategory" });
    setModals((prev) => ({ ...prev, additionalFields: true }));
  }, []);

  const openTertiaryFieldsModal = useCallback(
    (category: string, subcategory: string, tertiary: string) => {
      setSelectedContext({ category, subcategory, tertiary, modalType: "tertiary" });
      setModals((prev) => ({ ...prev, additionalFields: true }));
    },
    []
  );

  const closeAdditionalFieldsModal = useCallback(() => {
    setModals((prev) => ({ ...prev, additionalFields: false }));
  }, []);

  const openEditCategoryModal = useCallback((categoryId: string) => {
    setSelectedContext((prev) => ({
      ...prev,
      category: categoryId,
      modalType: "category",
    }));
    setModals((prev) => ({ ...prev, editCategory: true }));
  }, []);

  const closeEditCategoryModal = useCallback(() => {
    setModals((prev) => ({ ...prev, editCategory: false }));
  }, []);

  const openEditSubcategoryModal = useCallback((categoryId: string, subcategoryId: string) => {
    setSelectedContext((prev) => ({
      ...prev,
      category: categoryId,
      subcategory: subcategoryId,
      modalType: "subcategory",
    }));
    setModals((prev) => ({ ...prev, editSubcategory: true }));
  }, []);

  const closeEditSubcategoryModal = useCallback(() => {
    setModals((prev) => ({ ...prev, editSubcategory: false }));
  }, []);

  const openEditTertiaryModal = useCallback(
    (categoryId: string, subcategoryId: string, tertiaryId: string) => {
      setSelectedContext((prev) => ({
        ...prev,
        category: categoryId,
        subcategory: subcategoryId,
        tertiary: tertiaryId,
        modalType: "tertiary",
      }));
      setModals((prev) => ({ ...prev, editTertiary: true }));
    },
    []
  );

  const closeEditTertiaryModal = useCallback(() => {
    setModals((prev) => ({ ...prev, editTertiary: false }));
  }, []);

  const openDeleteCategoryModal = useCallback((categoryId: string) => {
    setSelectedContext({
      category: categoryId,
      subcategory: "",
      modalType: "category",
      deleteItemType: "category",
    });
    setModals((prev) => ({ ...prev, deleteConfirmation: true }));
  }, []);

  const openDeleteSubcategoryModal = useCallback(
    (categoryId: string, subcategoryId: string) => {
      setSelectedContext({
        category: categoryId,
        subcategory: subcategoryId,
        modalType: "subcategory",
        deleteItemType: "subcategory",
      });
      setModals((prev) => ({ ...prev, deleteConfirmation: true }));
    },
    []
  );

  const openDeleteTertiaryModal = useCallback(
    (categoryId: string, subcategoryId: string, tertiaryId: string) => {
      setSelectedContext({
        category: categoryId,
        subcategory: subcategoryId,
        tertiary: tertiaryId,
        modalType: "tertiary",
        deleteItemType: "tertiary",
      });
      setModals((prev) => ({ ...prev, deleteConfirmation: true }));
    },
    []
  );

  const closeDeleteConfirmationModal = useCallback(() => {
    setModals((prev) => ({ ...prev, deleteConfirmation: false }));
  }, []);

  const closeAllModals = useCallback(() => {
    setModals({
      addCategory: false,
      addSubcategory: false,
      addTertiary: false,
      additionalFields: false,
      editCategory: false,
      editSubcategory: false,
      editTertiary: false,
      deleteConfirmation: false,
    });
  }, []);

  return {
    modals,
    selectedContext,
    openAddCategoryModal,
    closeAddCategoryModal,
    openAddSubcategoryModal,
    closeAddSubcategoryModal,
    openAddTertiaryModal,
    closeAddTertiaryModal,
    openAdditionalFieldsModal,
    openCategoryFieldsModal,
    openSubcategoryFieldsModal,
    openTertiaryFieldsModal,
    closeAdditionalFieldsModal,
    openEditCategoryModal,
    closeEditCategoryModal,
    openEditSubcategoryModal,
    closeEditSubcategoryModal,
    openEditTertiaryModal,
    closeEditTertiaryModal,
    openDeleteCategoryModal,
    openDeleteSubcategoryModal,
    openDeleteTertiaryModal,
    closeDeleteConfirmationModal,
    closeAllModals,
  };
};
